import { FaGlobe, FaPhoneAlt, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import hospitalLogoWhite from '../assets/logo/mukti-logo-white.png'; 

const PatientStatementPrint = ({ className, patient, transactions, filterType }) => {
  
  // 1. Calculate Totals
  const totals = transactions.reduce(
    (acc, item) => ({
      amount: acc.amount + (parseFloat(item.amount) || 0),
      paid: acc.paid + (parseFloat(item.paid) || 0),
      due: acc.due + (parseFloat(item.amount - item.paid) || 0),
    }),
    { amount: 0, paid: 0, due: 0 }
  );

  return (
    <div className={`${className} hidden`}>
      
      {/* --- PRINT STYLES --- */}
      <style>
        {`
          @media print {
            @page {
              size: A4;
              margin: 0;
            }
            body * {
              visibility: hidden;
            }
            .print-wrapper, .print-wrapper * {
              visibility: hidden;
            }
            .${className}, .${className} * {
              visibility: visible;
            }
            .${className} {
              display: block !important;
            }
            .print-container {
              position: relative;
              width: 100%;
              height: 100vh;
              font-family: 'Inter', sans-serif;
              color: #333;
            }
            
            /* Forces background colors to print */
            .print-bg-force {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            /* Header Gradient */
            .header-gradient {
              background: linear-gradient(120deg, #323290, #009650) !important;
            }

            /* Footer Shape (Green Triangle) */
            .footer-shape {
              content: '';
              position: absolute;
              top: 0;
              right: 0;
              width: 150px;
              height: 100%;
              background-color: #009650 !important;
              clip-path: polygon(25% 0%, 100% 0%, 100% 100%, 0% 100%);
            }

            /* Table Styles */
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 9pt;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #323290 !important;
              color: #fff !important;
              font-weight: bold;
              text-transform: uppercase;
              font-size: 8pt;
            }
            tbody tr:nth-child(even) { 
              background-color: #f7f9ff !important; 
            }
            
            /* Alignment helpers */
            .text-right { text-align: right; }
            .text-center { text-align: center; }

            /* Watermark */
            .watermark {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-45deg);
              font-size: 80pt;
              font-weight: bold;
              color: rgba(0, 0, 0, 0.03);
              pointer-events: none;
              z-index: -1;
              white-space: nowrap;
            }
          }
        `}
      </style>

      <div className="print-container">
        
        {/* --- 1. HEADER --- */}
        <div className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between p-4 px-[15mm] text-white header-gradient print-bg-force">
          <div className="logo-container">
            {/* Ensure the image path is correct */}
            <img src={hospitalLogoWhite} alt="Hospital Logo" className="h-12 w-auto object-contain" />
          </div>
          <div className="text-right text-[9pt] leading-tight">
            <p className="font-bold text-[11pt] mb-1">Mukti Hospital</p>
            <p>+880 1601-666893</p>
            <p>muktihospitalpvtlimited@gmail.com</p>
            <p>https://muktihospital.com</p>
          </div>
        </div>

        {/* --- 2. MAIN CONTENT WRAPPER --- */}
        <div className="px-[15mm] pt-[100px] pb-[100px]">
          
          {/* Watermark */}
          <div className="watermark">Mukti Hospital</div>

          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-[#323290] underline uppercase tracking-wide inline-block mt-6 pb-1">
              Patient Statement
            </h1>
            <p className="text-[9pt] text-gray-500 mt-2">
              Report Type: <span className="font-bold text-gray-700">{filterType} Transactions</span> | 
              Printed On: <span className="font-bold text-gray-700">{new Date().toLocaleDateString("en-GB")}</span>
            </p>
          </div>

          {/* Patient Info Block */}
          <div className="flex justify-between mb-6 text-[10pt] border-b border-dashed border-gray-300 pb-4">
            <div className="w-[60%]">
              <table className="w-full !border-none !m-0">
                <tbody className="!border-none">
                  <tr className="!bg-transparent"><td className="!border-none !p-1 w-28 font-bold text-gray-600">Patient Name:</td><td className="!border-none !p-1 font-bold text-gray-900 text-[11pt]">{patient?.name}</td></tr>
                  <tr className="!bg-transparent"><td className="!border-none !p-1 w-28 font-bold text-gray-600">Patient ID:</td><td className="!border-none !p-1 font-mono text-gray-800">{patient?.id}</td></tr>
                  <tr className="!bg-transparent"><td className="!border-none !p-1 w-28 font-bold text-gray-600">Phone:</td><td className="!border-none !p-1">{patient?.phoneNumber}</td></tr>
                </tbody>
              </table>
            </div>
            <div className="w-[40%] text-right">
               <div className="bg-[#f0f9f4] p-3 rounded border border-[#009650]/20 inline-block text-left print-bg-force">
                  <p className="text-[9pt] text-gray-500">Current Due Amount</p>
                  <p className="text-xl font-bold text-[#009650]">৳ {totals.due.toLocaleString()}</p>
               </div>
            </div>
          </div>

          {/* Transaction Table */}
          <table className="print-bg-force">
            <thead>
              <tr>
                <th className="text-center w-[15%]">Date</th>
                <th className="text-center w-[20%]">Invoice No</th>
                <th className="text-center w-[20%]">Bill Type</th>
                <th className="text-right w-[15%]">Bill Amount</th>
                <th className="text-right w-[15%]">Paid</th>
                <th className="text-right w-[15%]">Due</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length > 0 ? (
                transactions.map((item, index) => (
                  <tr key={index}>
                    <td className="text-center">{new Date(item.date).toLocaleDateString("en-GB")}</td>
                    <td className="text-center font-mono text-[8pt]">{item.billNo || item.id}</td>
                    <td className="text-center">
                        <span className="bg-gray-100 px-2 py-0.5 rounded text-[8pt] print-bg-force">
                            {item.type.replace(' Bill', '')}
                        </span>
                    </td>
                    <td className="text-right font-medium">{parseFloat(item.amount).toLocaleString()}</td>
                    <td className="text-right text-green-700">{parseFloat(item.paid).toLocaleString()}</td>
                    <td className="text-right font-bold text-red-600">
                      {(parseFloat(item.amount) - parseFloat(item.paid)) > 0 
                        ? (parseFloat(item.amount) - parseFloat(item.paid)).toLocaleString() 
                        : '-'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-6 italic text-gray-500">No transactions found for this period.</td>
                </tr>
              )}
            </tbody>
            {/* Table Footer / Totals */}
            <tfoot>
              <tr className="bg-gray-100 font-bold print-bg-force">
                <td colSpan="3" className="text-right uppercase text-[9pt] pt-3 pb-3">Grand Total:</td>
                <td className="text-right pt-3 pb-3">{totals.amount.toLocaleString()}</td>
                <td className="text-right pt-3 pb-3">{totals.paid.toLocaleString()}</td>
                <td className="text-right pt-3 pb-3 text-[#323290]">{totals.due.toLocaleString()}</td>
              </tr>
            </tfoot>
          </table>

          {/* Signatures */}
          <div className="flex justify-between items-end mt-24">
            <div className="text-center">
              <div className="border-t border-gray-800 w-40"></div>
              <p className="text-[9pt] mt-1 font-medium">Patient Signature</p>
            </div>
            <div className="text-center">
              <div className="border-t border-gray-800 w-40"></div>
              <p className="text-[9pt] mt-1 font-medium">Authority Signature</p>
            </div>
          </div>

          <div className="text-center mt-12 text-[8pt] text-gray-400">
             <p>This is a computer-generated document. Valid without a seal.</p>
          </div>

        </div>

        {/* --- 3. FOOTER --- */}
        <div className="absolute bottom-0 left-0 right-0">
          <div className="relative bg-[#e8f0ed] p-3 px-[15mm] print-bg-force">
            <div className="footer-shape print-bg-force"></div>
            <div className="relative z-10 grid grid-cols-2 gap-x-5 gap-y-1 text-[8pt] text-gray-800 font-medium">
              <p className="flex items-center gap-2"><FaPhoneAlt size={10} /> +880 1601-666893</p>
              <p className="flex items-center gap-2"><FaMapMarkerAlt size={10} /> Race Course, Sasongacha, Cumilla</p>
              <p className="flex items-center gap-2"><FaEnvelope size={10} /> muktihospitalpvtlimited@gmail.com</p>
              <p className="flex items-center gap-2"><FaGlobe size={10} /> https://muktihospital.com</p>
            </div>
          </div>
          <div className="flex items-center justify-between bg-[#2d3748] p-2 px-[15mm] text-[7pt] text-white print-bg-force">
            <p>Software Powered By: Microters</p>
            <div className="flex items-center gap-4">
              <p className="flex items-center gap-1.5"><FaGlobe size={9}/> microters.com</p>
              <p className="flex items-center gap-1.5"><FaPhoneAlt size={9}/> +880 162-519-2766</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default PatientStatementPrint;
